import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Dialog, DialogTrigger } from '@/components/ui/Dialog';
import { Loader2, Plus, Info } from 'lucide-react';
import { useCreditPlans, type CreditPlanFormData, type CreditPlan } from './hooks/useCreditPlans';
import { CreditPlanFormDialog } from './components/CreditPlanFormDialog';
import { CreditPlanTable } from './components/CreditPlanTable';

interface CreditPlanManagementProps {
  serviceSlug: string;
  serviceName: string;
}

const CreditPlanManagement: React.FC<CreditPlanManagementProps> = ({ serviceSlug, serviceName }) => {
  const {
    plans,
    loading,
    error,
    setError,
    fetchPlans,
    createPlan,
    updatePlan,
    deletePlan,
  } = useCreditPlans(serviceSlug);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<CreditPlan | null>(null);
  const [formData, setFormData] = useState<CreditPlanFormData>({
    plan_name: '',
    monthly_base: '',
    credit_per_call: '',
    billing_type: 'free',
    is_active: true,
  });
  const [initialFormData, setInitialFormData] = useState<CreditPlanFormData | null>(null);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const resetForm = () => {
    setError('');
    setFormData({
      plan_name: '',
      monthly_base: '',
      credit_per_call: '',
      billing_type: 'free',
      is_active: true,
    });
  };

  const handleCreate = async () => {
    if (!formData.plan_name) {
      setError('필수 필드를 모두 입력해주세요.');
      return;
    }

    try {
      await createPlan(formData);
      setShowCreateDialog(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || '크레딧 플랜 생성에 실패했습니다.');
    }
  };

  const handleEdit = (plan: CreditPlan) => {
    setError('');
    setEditingPlan(plan);
    const monthlyBase = plan.monthly_base ?? plan.quota_limit ?? 0;
    const perCall = plan.price_per_unit ?? 0;
    // 소수점이 있는 경우 그대로 유지, 없으면 정수로 표시
    const monthlyBaseStr = monthlyBase % 1 === 0 ? String(monthlyBase) : String(monthlyBase);
    const perCallStr = perCall % 1 === 0 ? String(perCall) : String(perCall);
    
    const initialData: CreditPlanFormData = {
      plan_name: plan.plan_name,
      monthly_base: monthlyBaseStr,
      credit_per_call: perCallStr,
      billing_type:
        monthlyBase === 0 && perCall === 0
          ? 'free'
          : monthlyBase === 0 && perCall > 0
          ? 'usage'
          : monthlyBase > 0 && perCall === 0
          ? 'flat'
          : 'base_plus_usage',
      is_active: plan.is_active,
    };
    
    setInitialFormData(initialData);
    setFormData(initialData);
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingPlan || !formData.plan_name) {
      setError('필수 필드를 모두 입력해주세요.');
      return;
    }

    try {
      await updatePlan(editingPlan.id, formData);
      setShowEditDialog(false);
      setEditingPlan(null);
      resetForm();
    } catch (err: any) {
      setError(err.message || '크레딧 플랜 수정에 실패했습니다.');
    }
  };

  const handleDelete = async (planId: number) => {
    if (!window.confirm('정말 이 크레딧 플랜을 삭제하시겠습니까?')) return;

    try {
      await deletePlan(planId);
    } catch (err: any) {
      setError(err.message || '크레딧 플랜 삭제에 실패했습니다.');
    }
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    resetForm();
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingPlan(null);
    setInitialFormData(null);
  };

  const handleResetForm = () => {
    if (initialFormData) {
      setFormData(initialFormData);
      setError('');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>서비스 요금제 관리</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            서비스: <span className="font-medium">{serviceName}</span>
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="mr-2 h-4 w-4" />
              플랜 추가
            </Button>
          </DialogTrigger>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">크레딧 환율 정보</p>
              <p>10,000원 = 100 크레딧 (1크레딧 = 100원)</p>
              <p className="mt-1 text-xs">서비스 이용 시 설정된 크레딧만큼 자동으로 차감됩니다.</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4">
            <AlertMessage type="error" message={error} onClose={() => setError('')} />
          </div>
        )}

        {loading && plans.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">크레딧 플랜이 없습니다. 새 플랜을 추가해주세요.</p>
        ) : (
          <CreditPlanTable
            plans={plans}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </CardContent>

      <CreditPlanFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        isEdit={false}
        editingPlan={null}
        formData={formData}
        onFormDataChange={setFormData}
        error={error}
        loading={loading}
        onSubmit={handleCreate}
        onClose={handleCloseCreateDialog}
      />

      <CreditPlanFormDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        isEdit={true}
        editingPlan={editingPlan}
        formData={formData}
        onFormDataChange={setFormData}
        error={error}
        loading={loading}
        onSubmit={handleUpdate}
        onClose={handleCloseEditDialog}
        onReset={handleResetForm}
        initialFormData={initialFormData}
      />
    </Card>
  );
};

export default CreditPlanManagement;
